import Payment from 'payment';
import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {isCreditCard} from 'validator';

import FormField from './FormField';
import {requiresAddressForCountry} from '../../../../utils/binRegions';
import {
  formatCreditCardNumber,
  formatCVC,
  formatExpirationDate,
  isValidExpirationDate,
} from '../../../../utils/helper/validateCreditCard';
import {
  isValidEmail,
  isAlphabetOnly,
  hasSpecialChars,
} from '../../../../utils/helper/validation';
import {generateCashiaHeadersV2} from '../../../../utils/hmac/cashiaAuthV2';
import {binLookup, BinLookupRequest} from '../api';

export type CardInfo = {
  firstName: string;
  lastName: string;
  email: string;
  cardNumber: string;
  expiryDate: string;
  cvv: string;
};

// Export the ref methods so parent components can access validation
export interface CardPaymentFormRef {
  validateAll: () => boolean;
  hasErrors: () => boolean;
  getErrors: () => Partial<Record<keyof CardInfo, string>>;
}

const expectedCvvLength = 3;
const expectedCardNumberLength = 16;

const CardPaymentForm = forwardRef<
  CardPaymentFormRef,
  {
    keyId: string;
    secretKey: string;
    cardInfo: CardInfo;
    setCardInfo: React.Dispatch<React.SetStateAction<CardInfo>>;
    onBinCheck?: (requiresAddress: boolean) => void;
  }
>((props, ref) => {
  const {keyId, secretKey, cardInfo, setCardInfo, onBinCheck} = props;

  // Internal error state - no longer passed from parent
  const [errors, setErrors] = useState<Partial<Record<keyof CardInfo, string>>>(
    {}
  );

  // Expose validation methods to parent via ref
  useImperativeHandle(ref, () => ({
    validateAll: () => {
      const newErrors: Partial<Record<keyof CardInfo, string>> = {};

      // Validate all fields
      if (!cardInfo.firstName) {
        newErrors.firstName = 'First name is required';
      } else if (
        hasSpecialChars(cardInfo.firstName) ||
        !isAlphabetOnly(cardInfo.firstName)
      ) {
        newErrors.firstName = 'Only letters are allowed';
      }

      if (!cardInfo.lastName) {
        newErrors.lastName = 'Last name is required';
      } else if (
        hasSpecialChars(cardInfo.lastName) ||
        !isAlphabetOnly(cardInfo.lastName)
      ) {
        newErrors.lastName = 'Only letters are allowed';
      }

      if (!cardInfo.email) {
        newErrors.email = 'Email is required';
      } else if (!isValidEmail(cardInfo.email)) {
        newErrors.email = 'Enter a valid email address';
      }

      if (!cardInfo.cardNumber) {
        newErrors.cardNumber = 'Card number is required';
      } else {
        const issuer = Payment.fns.cardType(cardInfo.cardNumber);
        if (issuer === 'amex') {
          newErrors.cardNumber = 'American express is not supported yet';
        } else if (
          cardInfo.cardNumber.replaceAll(' ', '').length <
          expectedCardNumberLength
        ) {
          newErrors.cardNumber = 'Card number has an invalid length';
        } else if (!isCreditCard(cardInfo.cardNumber)) {
          newErrors.cardNumber = 'Please enter a valid card number';
        }
      }

      if (!cardInfo.expiryDate) {
        newErrors.expiryDate = 'Expiry date is required';
      } else if (!isValidExpirationDate(cardInfo.expiryDate)) {
        newErrors.expiryDate = 'Enter a valid future date';
      }

      if (!cardInfo.cvv) {
        newErrors.cvv = 'CVV is required';
      } else if (cardInfo.cvv.length !== expectedCvvLength) {
        newErrors.cvv = `Must be ${expectedCvvLength} digits`;
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },

    hasErrors: () => {
      return Object.values(errors).some((error) => error !== '');
    },

    getErrors: () => errors,
  }));

  const validateCvvLength = (value: string) => {
    if (!value?.length) {
      setErrors((prev) => ({...prev, cvv: ''}));
      return;
    }
    const cvvLength = value.length;
    setErrors((prev) => ({
      ...prev,
      cvv:
        cvvLength !== expectedCvvLength
          ? `Must be ${expectedCvvLength} digits`
          : '',
    }));
  };

  const validateCreditCard = (number: string) => {
    if (!number.length) {
      setErrors((prev) => ({...prev, cardNumber: ''}));
      return;
    }

    const issuer = Payment.fns.cardType(number);
    if (issuer === 'amex') {
      setErrors((prev) => ({
        ...prev,
        cardNumber: 'American express is not supported yet',
      }));
      return;
    }

    if (number.replaceAll(' ', '').length < expectedCardNumberLength) {
      setErrors((prev) => ({
        ...prev,
        cardNumber: 'Card number has an invalid length',
      }));
      return;
    }

    if (!isCreditCard(number)) {
      setErrors((prev) => ({
        ...prev,
        cardNumber: 'Please enter a valid card number',
      }));
      return;
    }

    setErrors((prev) => ({...prev, cardNumber: ''}));
  };

  const validateExpiry = (value: string) => {
    if (value && !isValidExpirationDate(value)) {
      setErrors((prev) => ({...prev, expiryDate: 'Enter a valid future date'}));
    } else {
      setErrors((prev) => ({...prev, expiryDate: ''}));
    }
  };

  const validateEmail = (value: string) => {
    if (!value) {
      setErrors((prev) => ({...prev, email: ''}));
      return;
    }
    if (!isValidEmail(value)) {
      setErrors((prev) => ({...prev, email: 'Enter a valid email address'}));
    } else {
      setErrors((prev) => ({...prev, email: ''}));
    }
  };

  const validateName = (field: 'firstName' | 'lastName', value: string) => {
    if (!value) {
      setErrors((prev) => ({...prev, [field]: ''}));
      return;
    }
    if (hasSpecialChars(value) || !isAlphabetOnly(value)) {
      setErrors((prev) => ({...prev, [field]: 'Only letters are allowed'}));
    } else {
      setErrors((prev) => ({...prev, [field]: ''}));
    }
  };

  const handleInputChange = function handleInputChange(
    field: keyof CardInfo,
    value: string
  ) {
    let formattedValue = value;

    switch (field) {
      case 'firstName':
      case 'lastName':
        // Only allow alphabetic characters, filter out others
        if ((!hasSpecialChars(value) && isAlphabetOnly(value)) || !value) {
          formattedValue = value.replaceAll(' ', '');
          setCardInfo({...cardInfo, [field]: formattedValue});
          validateName(field, formattedValue);
        }
        return; // Early return to prevent duplicate state update

      case 'email':
        formattedValue = value.replaceAll(' ', '');
        setCardInfo({...cardInfo, [field]: formattedValue});
        validateEmail(formattedValue);
        return;

      case 'cardNumber':
        formattedValue = formatCreditCardNumber(value);
        setCardInfo({...cardInfo, [field]: formattedValue});
        validateCreditCard(value);
        return;

      case 'expiryDate':
        formattedValue = formatExpirationDate(value);
        setCardInfo({...cardInfo, [field]: formattedValue});
        validateExpiry(value);
        return;

      case 'cvv':
        formattedValue = formatCVC(value);
        setCardInfo({...cardInfo, [field]: formattedValue});
        validateCvvLength(value);
        return;

      default:
        break;
    }
  };

  // Validate CVV when card number changes (similar to original component)
  useEffect(() => {
    if (cardInfo.cardNumber && cardInfo.cvv) {
      validateCvvLength(cardInfo.cvv);
    }
  }, [cardInfo.cardNumber, cardInfo.cvv]);

  useEffect(() => {
    const trimmedNumber = cardInfo.cardNumber.replace(/\s/g, '');

    if (trimmedNumber.length < expectedCardNumberLength) return;

    const delay = setTimeout(() => {
      const requestBody: BinLookupRequest = {
        cardNumber: trimmedNumber,
      };
      const headers = generateCashiaHeadersV2({
        secret: secretKey,
        keyId: keyId,
        requestBody: requestBody,
        method: 'POST',
      });
      binLookup(requestBody, headers)
        .then((res) => {
          onBinCheck?.(
            requiresAddressForCountry(res.issuerInformation.country)
          );
        })
        .catch(() => {
          onBinCheck?.(true); // Default to true if lookup fails
        });
    }, 600); // 600ms debounce

    return () => clearTimeout(delay);
  }, [cardInfo.cardNumber, onBinCheck, keyId, secretKey]);

  return (
    <div className="w-full">
      <div className="block">
        <FormField
          placeholder="First Name"
          value={cardInfo.firstName}
          onChange={(value) => handleInputChange('firstName', value)}
          error={errors.firstName}
          className={`mb-4 ${
            errors.firstName
              ? 'w-full bg-surfaceNegative'
              : 'w-full border rounded-md border-subtleGrey'
          } `}
        />

        <FormField
          placeholder="Last Name"
          value={cardInfo.lastName}
          onChange={(value) => handleInputChange('lastName', value)}
          error={errors.lastName}
          className={`mb-4 ${
            errors.lastName
              ? 'w-full bg-surfaceNegative'
              : 'w-full border rounded-md border-subtleGrey'
          } `}
        />

        <FormField
          placeholder="Email"
          value={cardInfo.email}
          type="email"
          onChange={(value) => handleInputChange('email', value)}
          error={errors.email}
          className={`mb-4 ${
            errors.email
              ? 'w-full bg-surfaceNegative'
              : 'w-full border rounded-md border-subtleGrey'
          } `}
        />
      </div>

      <FormField
        placeholder="Card Number"
        value={cardInfo.cardNumber}
        onChange={(value) => handleInputChange('cardNumber', value)}
        error={errors.cardNumber}
        maxLength={19}
        className={`mb-4 ${
          errors.cardNumber
            ? 'w-full bg-surfaceNegative'
            : 'w-full border rounded-md border-subtleGrey'
        } `}
      />

      <div className="flex gap-4">
        <div className="w-1/2 flex flex-col justify-between">
          <div className="text-[14px] font-semibold text-borderGreyHover mb-2">
            Expiry
          </div>

          <FormField
            placeholder="MM/YY"
            value={cardInfo.expiryDate}
            onChange={(value) => handleInputChange('expiryDate', value)}
            error={errors.expiryDate}
            className={`${
              errors.expiryDate
                ? 'w-full bg-surfaceNegative'
                : 'w-full border rounded-md border-subtleGrey'
            } `}
          />
        </div>
        <div className="w-1/2 flex-col justify-between">
          <div className="text-[14px] font-semibold text-borderGreyHover mb-2">
            Security Code (CVV)
          </div>
          <FormField
            placeholder="CVV"
            value={cardInfo.cvv}
            onChange={(value) => handleInputChange('cvv', value)}
            error={errors.cvv}
            type="password"
            maxLength={expectedCvvLength}
            className={`${
              errors.cvv
                ? 'w-full bg-surfaceNegative'
                : 'w-full border rounded-md border-subtleGrey'
            } `}
          />
        </div>
      </div>
    </div>
  );
});

CardPaymentForm.displayName = 'CardPaymentForm';

export default CardPaymentForm;
